import { PayloadAction } from '@reduxjs/toolkit';
import { array, either, option, record, separated, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { AppEvent, MultiCurrencyAmount, Route } from '@mews-distributor/common';
import { ConfigurationResult, Enterprise } from '@mews-distributor/data-access';

import {
    AppClockService,
    DateService,
    DateTimePart,
    GainsightService,
    LoggerAdapter,
    SentryReporterService,
    TrackingConsentsService,
    WallClockDateService,
} from '@mews/framework';

import { ActionWithPayload } from '@mews/framework/redux';
import {
    APP_CLOSED,
    APP_INITIALIZATION_SUCCEEDED,
    APP_OPENED,
    CURRENCY_CHANGE_SUCCEEDED,
    LANGUAGE_SET,
    ROUTE_SET,
} from '../../modules/app/actionTypes';
import { getCurrencyCode, getLanguageCode, getReservationIdsFromQuery, isAppOpened } from '../../modules/app/selectors';
import { FETCH_CONFIGURATIONS_SUCCEEDED } from '../../modules/configuration/actionTypes';
import {
    getGoogleTagManagerContainerId,
    getThemeVariant,
    getVersion,
    hasIncludeEmailInPurchaseEventEnabled,
    isStandalone,
} from '../../modules/configuration/selectors';
import { isAgeCategoryAdultClassification } from '../../modules/entities/ageCategories/selectors';
import { getChainName } from '../../modules/entities/chains/selectors';
import { getSelectedCityName } from '../../modules/entities/cities/selectors';
import {
    getEnterpriseChainId,
    getEnterpriseDefaultCurrencyCode,
    getEnterpriseDefaultLanguageCode,
    getEnterpriseName,
    getEnterprisePricing,
    getEnterprisePricingModeKey,
    getEnterpriseTimezone,
    hasEnterpriseNetPricing,
} from '../../modules/entities/enterprises/selectors';
import { convertPrice } from '../../modules/entities/exchangeRates/viewSelectors';
import { getProductEnterpriseId, getProductName } from '../../modules/entities/products/selectors';
import { getRateName, getRateServiceId } from '../../modules/entities/rates/selectors';
import { RESERVATION_COUNT_CHANGED } from '../../modules/entities/reservations/actionTypes';
import {
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getReservations,
    getReservationsInTrackingFormat,
    getTotalCostWithSeparatedCurrencyGross,
} from '../../modules/entities/reservations/selectors';
import { Reservation, Reservations } from '../../modules/entities/reservations/types';
import { getServiceEnterpriseId, isServiceDayUse } from '../../modules/entities/services/selectors';
import { GTM_CONFIGURATION_SET } from '../../modules/global/actionTypes';
import { localizeProperty } from '../../modules/globalization/selectors';
import {
    isPurchaseEventTriggered,
    preserveGA4Cookies,
    setPurchaseEventTriggered,
} from '../../modules/googleAnalytics/utils';
import {
    AVAILABLE_OTHER_DATES_SUGGESTED,
    NO_AVAILABLE_OTHER_DATES_SUGGESTED,
    OTHER_DATES_SELECTED,
    OTHER_DATES_SUGGESTED,
} from '../../modules/otherDatesSuggestions/actionTypes';
import { getPricingHash } from '../../modules/pricing/getPricingHash';
import { getPricingValueByHash } from '../../modules/pricing/selectors';
import { getPromotedServiceName } from '../../modules/promotedServices';
import { getReservationGroupName } from '../../modules/reservationGroups/selectors';
import { getCategories, getCategory, getCategoryName } from '../../modules/resourceCategories/selectors';
import { CreateRoomOccupancyChangedActionOptions } from '../../modules/resourceOrder/actions';
import {
    END_DATE_SET,
    PRODUCT_ADDED,
    PRODUCT_REMOVED,
    RESOURCE_ORDER_ADDED,
    ROOM_OCCUPANCY_CHANGED,
    ROOM_SELECTED,
    SPACE_TYPE_COUNT_CHANGED,
    START_DATE_SET,
    VOUCHER_CODE_SET,
} from '../../modules/resourceOrder/actionTypes';
import {
    getEndDate,
    getSelectedHotelId,
    getStartDate,
    getVoucherCode,
    isHotelSelected,
} from '../../modules/resourceOrder/selectors';
import { getSelectedRoomAvailableRatePrices } from '../../selectors/availability';
import {
    getSelectedEnterpriseDefaultLanguageCode,
    getSelectedEnterpriseDefaultLanguageName,
    getSelectedEnterpriseDefaultRateCurrencyCode,
    getSelectedEnterpriseName,
} from '../../selectors/views/selectedEnterprise/selectedEnterprise';
import { getReservationsIntegrationData, parseReservationDate } from '../../selectors/withContainerUse';
import { TRACKING_CONSENTS_READY, TRACKING_CONSENTS_SET } from '../../services/trackingConsents/actionTypes';
import { getHash } from '../../utils/getHash';
import { getReservationsTotalMultiCurrencyAmount } from '../../views/checkout/data/viewSelectors';
import {
    CHECKOUT_PREPARATION_SKIPPED,
    CHECKOUT_PREPARATION_SUCCEEDED,
    CHECKOUT_REQUEST_STARTED,
    CHECKOUT_REQUEST_SUCCEEDED,
} from '../../views/checkout/slices/checkout/actionTypes';
import { getConfirmation, getReservationGroupIntegrationData } from '../../views/confirmation/data/selectors';
import { OrderedProduct } from '../../views/confirmation/data/types';
import { CATEGORY_LIGHTBOX_OPENED, CATEGORY_VIEWED } from '../../views/spaceCategories/actionTypes';
import { createTypedEvents } from '../eventHandlers/typedEvents';
import { createGA4EventService, Item } from '../ga4EventService';
import {
    CATEGORIES_LOADED,
    confirmationLoaded,
    DETAILS_LOADED,
    FLEXIBLE_RATES_FILTER_CHANGED,
    PRODUCTS_LOADED,
    PROMOTED_SERVICES_LOADED,
    PROPERTIES_LOADED,
    RATES_LOADED,
    SUMMARY_LOADED,
    VOUCHER_CODE_SUBMITTED,
} from '../gtmActions';
import { getBookerEmail, getCustomerEmail } from '../../views/checkout/data/selectors';
import { logSentryError } from '../../sentry/sentry';
import { updateObservabilityLabels } from '../../observability/actions';
import { checkGTMBlocked } from './actions';
import { injectGoogleTagManager } from './injectGoogleTagManager';
import { getCategoryItem, getProductItem } from './integrationsSelectors';
import { getGenericPurchaseEventData } from './purchaseEvent/transformation';
import { PurchaseEventReservation } from './purchaseEvent/types';
import { CategoriesLoadedPayload } from './types';

const EVENT_PREFIX = 'distributor';
const LOAD_TIMEOUT = 2000;
const LOAD_TRY_INTERVAL = 200;
const defaultMewsLanguageCode = 'en-US';

const initialEventData = {
    variant: undefined,
};

interface IntegrationsMiddlewareProps {
    context: Window;
    trackingConsentsService: TrackingConsentsService;
    sentryReporterService: SentryReporterService;
    loggerService: LoggerAdapter;
    azureLoggerService: LoggerAdapter;
    appClockService: AppClockService;
    gainsightService: GainsightService;
}

export function integrationsMiddleware({
    context,
    trackingConsentsService,
    sentryReporterService,
    loggerService,
    azureLoggerService,
    appClockService,
    gainsightService,
}: IntegrationsMiddlewareProps) {
    let gtmLoaded = false;
    let hasTagManager = false;
    let trackingConsentsReady = false;
    let eventQueue: $TSFixMe = [];

    return (store: $TSFixMe) => {
        function productEventHandler(action: $TSFixMe, event: $TSFixMe) {
            const { productId } = action.payload;
            const state = store.getState();
            const defaultLanguageCode = getSelectedEnterpriseDefaultLanguageCode(state, {});
            const property = getProductName(state, { id: productId });
            const productName = localizeProperty(state, { property, languageCode: defaultLanguageCode });

            triggerLegacyGtmEvent(event, {
                productId,
                productName,
            });
        }

        function checkoutPreparedHandler() {
            triggerLegacyGtmEvent(
                AppEvent.BookingPrepared,
                getTotalCostWithSeparatedCurrencyGross(store.getState(), {})
            );
        }

        const triggerLegacyGtmEvent = triggerEvent(prefix);
        const triggerGtmEvent = triggerEvent((eventName: string) => `ga4_${eventName}`);
        const ga4EventService = createGA4EventService({
            pushEvent: (event, options) => {
                enqueueOrPushEvent({ ecommerce: null });
                enqueueOrPushEvent(event, options);
            },
        });

        interface GetPriceFromAmountOrConvertProps {
            multiCurrencyAmount: MultiCurrencyAmount;
            targetCurrencyCode: string;
            pricingModeKey: 'grossValue' | 'netValue';
            enterpriseId: string;
        }

        const getPriceFromAmountOrConvert = ({
            multiCurrencyAmount,
            targetCurrencyCode,
            pricingModeKey,
            enterpriseId,
        }: GetPriceFromAmountOrConvertProps): number => {
            if (multiCurrencyAmount[targetCurrencyCode] != null) {
                return multiCurrencyAmount[targetCurrencyCode][pricingModeKey];
            }

            let sourceCurrencyCode = 'EUR';
            if (multiCurrencyAmount[sourceCurrencyCode] == null) {
                sourceCurrencyCode = Object.keys(multiCurrencyAmount)[0];
            }

            const state = store.getState();
            return convertPrice(state, {
                value: multiCurrencyAmount[sourceCurrencyCode][pricingModeKey],
                currencyCode: sourceCurrencyCode,
                enterpriseId,
                targetCurrencyCode,
                sourceCurrencyCode,
            });
        };

        const getGA4ItemsData = (reservation: Reservation, count: number) => {
            const state = store.getState();

            const {
                rateId,
                roomId: categoryId,
                productIds,
                occupancyData,
                voucherCode,
                startDate,
                endDate,
                hotelId: enterpriseId,
                startTimeUtc,
                endTimeUtc,
            } = reservation;
            const languageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });

            const enterpriseNames = getEnterpriseName(state, { enterpriseId });
            const cityNames = getSelectedCityName(state);
            const categoryNames = getCategoryName(state, { categoryId });
            const rateNames = getRateName(state, { rateId });
            const enterpriseName = localizeProperty(state, {
                property: enterpriseNames,
                languageCode,
            });
            const rateName = localizeProperty(state, { property: rateNames, languageCode });
            const categoryName = localizeProperty(state, { property: categoryNames, languageCode });
            const cityName = localizeProperty(state, { property: cityNames, languageCode });

            const enterpriseCurrency = getEnterpriseDefaultCurrencyCode(state, { enterpriseId });

            const pricing = getPricingValueByHash(state, {
                pricingHash: getPricingHash({
                    productIds,
                    occupancyData,
                    voucherCode,
                    categoryId,
                    startDate: startTimeUtc ?? startDate,
                    endDate: endTimeUtc ?? endDate,
                    enterpriseId,
                }),
            });

            const pricingModeKey = getEnterprisePricingModeKey(state, { enterpriseId });

            const rateTotalPrice = pricing.rates[reservation.rateId].price.totalAmount;
            const quantity = Math.abs(count);
            const chainId = getEnterpriseChainId(state, { enterpriseId });
            const chainName = getChainName(state, { chainId });
            const productItems = pipe(
                reservation.productIds,
                array.filterMap((productId: string) => {
                    const productNames = getProductName(state, { id: productId });
                    const productName = localizeProperty(state, {
                        property: productNames,
                        languageCode,
                    });
                    return option.fromNullable(
                        getProductItem({
                            state,
                            id: productId,
                            name: productName,
                            quantity,
                            loggerService,
                        })
                    );
                })
            );

            const priceWithItems = getPriceFromAmountOrConvert({
                multiCurrencyAmount: rateTotalPrice,
                targetCurrencyCode: enterpriseCurrency,
                // @ts-expect-error $TSFixMeError: Type 'null'
                pricingModeKey,
                enterpriseId,
            });
            const priceOfItems = productItems.reduce((prev, item) => prev + item.price * item.quantity, 0);
            const price = priceWithItems - priceOfItems;

            return [
                // TODO: This could be replaced with getCategoryItem(...) with few updates
                {
                    item_id: reservation.roomId,
                    quantity,
                    item_name: categoryName,
                    item_variant: rateName,
                    item_category: 'Reservation',
                    item_category2: enterpriseName,
                    item_category3: cityName,
                    item_brand: chainName ?? undefined,
                    affiliation: localizeProperty(state, {
                        property: enterpriseNames,
                        languageCode,
                    }),
                    coupon: voucherCode,
                    currency: enterpriseCurrency,
                    price,
                },
                ...productItems,
            ];
        };

        const triggerChangeReservationCountGA4Event = (reservation: Reservation, count: number) => {
            const items = getGA4ItemsData(reservation, count);

            const value = items.reduce((prev, item) => prev + item.price * item.quantity, 0);
            const currency = items[0]?.currency;

            if (count > 0) {
                ga4EventService.addToCart({ items, currency, value });
            } else {
                ga4EventService.removeFromCart({ items, currency, value });
            }
        };

        const triggerGA4PurchaseEvent = ({
            orderedProducts,
            reservations: confirmedReservations,
            totalAmount,
            reservationOwnerEmail,
        }: {
            orderedProducts: OrderedProduct[];
            reservations: PurchaseEventReservation[];
            totalAmount: MultiCurrencyAmount | null;
            reservationOwnerEmail?: string | null;
        }) => {
            const state = store.getState();
            const { reservationGroupId } = getConfirmation(state);
            // @ts-expect-error $TSFixMeError: Type 'string | null' is not assignable to type 'string'.
            const reservationGroupName = getReservationGroupName(state, { reservationGroupId });
            const enterpriseId = getSelectedHotelId(state);
            const isNetPricing = hasEnterpriseNetPricing(state, { enterpriseId });
            const enterpriseNames = getSelectedEnterpriseName(state, {});
            const languageCode = getSelectedEnterpriseDefaultLanguageCode(state, {});
            const currencyCode = getSelectedEnterpriseDefaultRateCurrencyCode(state, {});
            const cityNames = getSelectedCityName(state);
            const currentReservationIds = getReservationIdsFromQuery(state) || [];
            const reservations = confirmedReservations.filter(({ id }) => {
                // [] means no argument provided (no availability block or result of reservationGroups/create)
                // all reservations are valid
                if (currentReservationIds.length === 0) {
                    return true;
                }
                return currentReservationIds.includes(id);
            });

            const cityName = localizeProperty(state, { property: cityNames, languageCode });
            const enterpriseName = localizeProperty(state, { property: enterpriseNames, languageCode });
            const enterpriseTimeZone = getEnterpriseTimezone(state, { enterpriseId });
            const enterprisePricing = getEnterprisePricing(state, { enterpriseId });
            const chainId = getEnterpriseChainId(state, { enterpriseId });
            const chainName = getChainName(state, { chainId });
            const includeEmailInPurchaseEvent = hasIncludeEmailInPurchaseEventEnabled(state);

            const parseDate = (dateString: string) =>
                pipe(parseReservationDate(dateString, enterpriseTimeZone), (date) =>
                    // @ts-expect-error $TSFixMeError: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
                    WallClockDateService.toTimezonedDate(date, enterpriseTimeZone)
                );

            const reservationItems = reservations.map(
                ({
                    id,
                    rateId,
                    roomName,
                    confirmationNumber,
                    endUtc,
                    startUtc,
                    occupancyData,
                    serviceTotalAmounts,
                }) => {
                    const startDate = parseDate(startUtc);
                    const endDate = parseDate(endUtc);

                    const dayDiff = DateService.diff(DateTimePart.Day)(endDate, startDate);
                    const checkInDateString = DateService.toISOString(startDate);
                    const checkOutDateString = DateService.toISOString(endDate);
                    const stayDuration = isServiceDayUse(state, { enterpriseId }) ? dayDiff + 1 : dayDiff;
                    const rateNames = getRateName(state, { rateId });
                    const rateName = localizeProperty(state, { property: rateNames, languageCode });
                    const personCountClassification = pipe(
                        occupancyData,
                        array.partitionMap(({ ageCategoryId, personCount }) =>
                            isAgeCategoryAdultClassification(state, { ageCategoryId })
                                ? either.left(personCount)
                                : either.right(personCount)
                        )
                    );
                    const adultCount = pipe(
                        separated.left(personCountClassification),
                        array.reduce(0, (acc, personCount) => acc + personCount)
                    );
                    const childCount = pipe(
                        separated.right(personCountClassification),
                        array.reduce(0, (acc, personCount) => acc + personCount)
                    );

                    const genericData = getGenericPurchaseEventData(state, {
                        amount: serviceTotalAmounts,
                        chainName,
                        cityName,
                        currencyCode,
                        enterpriseName,
                        enterprisePricing,
                        id,
                        isNetPricing,
                        itemCategory: 'Reservation',
                        languageCode,
                        propertyName: roomName,
                        quantity: 1,
                        reservationGroupId,
                        reservationGroupName,
                    });

                    return {
                        ...genericData,
                        item_variant: rateName,
                        confirmationNumber,
                        reservationRateName: rateName,
                        checkInDate: checkInDateString,
                        checkOutDate: checkOutDateString,
                        stayDuration,
                        adults: adultCount,
                        children: childCount,
                    };
                }
            );

            const orderedProductItems = orderedProducts.map(({ productId, name: propertyName, amounts, quantity }) =>
                getGenericPurchaseEventData(state, {
                    amount: amounts,
                    chainName,
                    cityName,
                    currencyCode,
                    enterpriseName,
                    enterprisePricing,
                    id: productId,
                    isNetPricing,
                    itemCategory: 'Product',
                    languageCode,
                    propertyName,
                    quantity,
                    reservationGroupId,
                    reservationGroupName,
                })
            );

            const items = [...reservationItems, ...orderedProductItems];

            const reservationIdsString = reservations
                .map(({ id }) => id)
                .sort()
                .join(',');
            if (reservationIdsString.length > 0) {
                const totalAdultCount = pipe(
                    reservationItems,
                    array.reduce(0, (acc, item) => acc + item.adults)
                );
                const totalChildCount = pipe(
                    reservationItems,
                    array.reduce(0, (acc, item) => acc + item.children)
                );

                const confirmedTotalAmount = totalAmount?.[currencyCode];
                const confirmedTotalAmountNet = confirmedTotalAmount?.netValue;
                const confirmedTotalAmountGross = confirmedTotalAmount?.grossValue;

                const value = isNetPricing ? confirmedTotalAmountNet : confirmedTotalAmountGross;
                const confirmedTotalTaxValue = pipe(
                    confirmedTotalAmount?.taxValues ?? [],
                    array.reduce(0, (acc, taxValue) => acc + taxValue.value)
                );
                ga4EventService.purchase({
                    // @ts-expect-error $TSFixMeError: Type '{ item_id: string; item_name: any; item_variant: any; item_category: string; item_category2: any; item_category3: ...
                    items,
                    affiliation: enterpriseName,
                    currency: currencyCode,
                    transactionId: getHash(reservationGroupId + reservationIdsString),
                    reservationGroupId,
                    reservationGroupName,
                    reservationIds: reservationIdsString,
                    chainName,
                    value: value ?? NaN,
                    netValue: confirmedTotalAmountNet,
                    grossValue: confirmedTotalAmountGross,
                    pricingMode: enterprisePricing,
                    roomCount: reservations.length,
                    adults: totalAdultCount,
                    children: totalChildCount,
                    tax: confirmedTotalTaxValue,
                    reservationOwnerEmail: includeEmailInPurchaseEvent ? reservationOwnerEmail : undefined,
                });
            }
        };

        const eventMap = {
            [CATEGORY_LIGHTBOX_OPENED]: () => triggerLegacyGtmEvent(AppEvent.CategoryLightboxOpened),
            [TRACKING_CONSENTS_READY]: () => {
                trackingConsentsReady = true;
                flushEventQueue();
            },
            [GTM_CONFIGURATION_SET]: () => initialize(),
            [APP_OPENED]: () => {
                triggerGtmEvent(AppEvent.ApplicationLoaded);
                triggerLegacyGtmEvent(AppEvent.Opened);
            },
            [APP_CLOSED]: () => triggerLegacyGtmEvent(AppEvent.Closed),
            // @ts-expect-error $TSFixMeError: Binding element 'trackingConsents' implicitly has an 'any' type.
            [TRACKING_CONSENTS_SET]: ({ payload: trackingConsents }) =>
                triggerLegacyGtmEvent(AppEvent.TrackingConsentsSet, trackingConsents),
            [FETCH_CONFIGURATIONS_SUCCEEDED]: ({ payload: { enterprises } }: PayloadAction<ConfigurationResult>) => {
                enterprises.forEach(({ id, name, defaultLanguageCode }: Enterprise) => {
                    const data = {
                        id,
                        name: name[defaultLanguageCode],
                    };
                    triggerLegacyGtmEvent(AppEvent.HotelLoaded, data);
                });
            },
            [APP_INITIALIZATION_SUCCEEDED]: () => {
                void injectGtmScripts();
                triggerLegacyGtmEvent(AppEvent.Loaded);

                const state = store.getState();
                const data = {
                    startDate: getStartDate(state),
                    endDate: getEndDate(state),
                    languageCode: getLanguageCode(state),
                    currencyCode: getCurrencyCode(state),
                    promoCode: getVoucherCode(state),
                };
                triggerLegacyGtmEvent(AppEvent.ConfigurationSet, data);

                const themeVariant = getThemeVariant(state);
                triggerLegacyGtmEvent(AppEvent.ThemeSet, { themeVariant });
            },
            [CHECKOUT_REQUEST_STARTED]: () => {
                const state = store.getState();
                const products = getReservationsInTrackingFormat(state);
                const reservations = getReservations(state);
                const enterpriseId = getSelectedHotelId(state);
                const items = pipe(
                    reservations,
                    record.collect(string.Ord)((orderId, reservation) =>
                        getGA4ItemsData(reservation, reservation.count)
                    ),
                    array.flatten
                );

                const totalValue = getReservationsTotalMultiCurrencyAmount(state);
                const enterpriseDefaultCurrency: string = getEnterpriseDefaultCurrencyCode(state, {
                    enterpriseId,
                });

                const value = getPriceFromAmountOrConvert({
                    multiCurrencyAmount: totalValue,
                    targetCurrencyCode: enterpriseDefaultCurrency,
                    pricingModeKey: 'grossValue',
                    enterpriseId,
                });

                ga4EventService.addPaymentInfo({
                    items,
                    value,
                    currency: enterpriseDefaultCurrency,
                    paymentType: 'PaymentCard',
                });

                const isIncludeEmailInPurchaseEventEnabled = hasIncludeEmailInPurchaseEventEnabled(state);
                const reservationOwnerEmail = getCustomerEmail(state) ?? getBookerEmail(state);
                const bookingCreatedData =
                    isIncludeEmailInPurchaseEventEnabled && reservationOwnerEmail != null
                        ? { reservationOwnerEmail }
                        : {};
                triggerGtmEvent(AppEvent.BookingCreated, bookingCreatedData);

                triggerLegacyGtmEvent(AppEvent.Checkout, {
                    ecommerce: {
                        checkout: {
                            actionField: { step: 1, option: 'Finish Booking' },
                            products,
                        },
                    },
                });
            },
            [CHECKOUT_REQUEST_SUCCEEDED]: () => {
                const state = store.getState();
                triggerLegacyGtmEvent(AppEvent.BookingFinished, getReservationGroupIntegrationData(state, {}));
                const reservationsData = getReservationsIntegrationData(state, {});
                reservationsData.forEach((r) => triggerLegacyGtmEvent(AppEvent.ReservationCreated, r));
            },
            [CHECKOUT_PREPARATION_SUCCEEDED]: checkoutPreparedHandler,
            [CHECKOUT_PREPARATION_SKIPPED]: checkoutPreparedHandler,
            [END_DATE_SET]: (action: ActionWithPayload<string, { endDate: string }>) =>
                triggerLegacyGtmEvent(AppEvent.EndDateSelected, { endDate: action.payload.endDate }),
            [LANGUAGE_SET]: (action: ActionWithPayload<string, { languageCode: string }>) =>
                triggerLegacyGtmEvent(AppEvent.LanguageCodeChanged, { languageCode: action.payload.languageCode }),
            [CURRENCY_CHANGE_SUCCEEDED]: (action: ActionWithPayload<string, { currencyCode: string }>) =>
                triggerLegacyGtmEvent(AppEvent.CurrencyCodeChanged, { currencyCode: action.payload.currencyCode }),
            [OTHER_DATES_SUGGESTED]: () => triggerLegacyGtmEvent(AppEvent.AlternativeDatesOffered),
            [OTHER_DATES_SELECTED]: (action: ActionWithPayload<string, { label: string }>) =>
                triggerLegacyGtmEvent(AppEvent.OfferedDatesSelected, { shiftLabel: action.payload.label }),
            [AVAILABLE_OTHER_DATES_SUGGESTED]: () => {
                const state = store.getState();
                triggerLegacyGtmEvent(AppEvent.AvailableAlternativeDatesOffered);
                triggerGtmEvent(AppEvent.AvailableAlternativeDatesOffered, {
                    startDate: getStartDate(state),
                    endDate: getEndDate(state),
                });
            },
            [VOUCHER_CODE_SUBMITTED]: ({ payload: { voucherCode } }: $TSFixMe) => {
                triggerGtmEvent(AppEvent.VoucherAdded, { voucherCode });
                ga4EventService.selectPromotion(voucherCode);
            },
            [PROPERTIES_LOADED]: ({
                payload: { availableEnterpriseCount, unavailableEnterpriseCount, cityNames },
            }: $TSFixMe) =>
                triggerGtmEvent(AppEvent.PropertiesLoaded, {
                    availableCount: availableEnterpriseCount,
                    unavailableCount: unavailableEnterpriseCount,
                    city: localizeProperty(store.getState(), {
                        property: cityNames,
                        languageCode: defaultMewsLanguageCode,
                    }),
                    page_location: '/properties',
                    page_title: 'Properties',
                }),
            [PRODUCTS_LOADED]: ({ payload: { productIds } }: ActionWithPayload<string, { productIds: string[] }>) => {
                const state = store.getState();

                const products = productIds.map((productId) => {
                    const enterpriseId = getProductEnterpriseId(state, { id: productId });
                    // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
                    const languageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
                    const productNames = getProductName(state, { id: productId });
                    const productName = localizeProperty(state, {
                        property: productNames,
                        languageCode,
                    }) as unknown as string;

                    return {
                        id: productId,
                        name: productName,
                    };
                });

                triggerGtmEvent(AppEvent.ProductsLoaded, { products });
                const productItems: Item[] = pipe(
                    products,
                    array.filterMap(({ id, name }) =>
                        option.fromNullable(getProductItem({ state, id, name, loggerService, quantity: 1 }))
                    )
                );
                ga4EventService.viewItemList({ items: productItems });
            },
            [PROMOTED_SERVICES_LOADED]: ({ payload: { promotedServiceIds, enterpriseId } }: $TSFixMe) => {
                const state = store.getState();
                const languageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
                triggerGtmEvent(AppEvent.PromotedServicesLoaded, {
                    promotedServices: promotedServiceIds.map((promotedServiceId: $TSFixMe) => {
                        const promotedServiceNames = getPromotedServiceName(state, { promotedServiceId });
                        const promotedServiceName = promotedServiceNames
                            ? localizeProperty(state, {
                                  property: promotedServiceNames,
                                  languageCode,
                              })
                            : '';

                        return {
                            id: promotedServiceId,
                            name: promotedServiceName,
                        };
                    }),
                });
            },
            [RATES_LOADED]: ({ payload: { rateIds } }: $TSFixMe) => {
                const state = store.getState();
                triggerGtmEvent(AppEvent.RatesLoaded, {
                    rates: rateIds.map((rateId: string) => {
                        const serviceId = getRateServiceId(state, { rateId });
                        // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
                        const enterpriseId = getServiceEnterpriseId(state, { serviceId });
                        // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
                        const languageCode = getEnterpriseDefaultLanguageCode(state, { enterpriseId });
                        const rateNames = getRateName(state, { rateId });
                        const rateName = localizeProperty(state, { property: rateNames, languageCode });

                        const ratePrices = getSelectedRoomAvailableRatePrices(state);
                        const currencyCode = getCurrencyCode(state);
                        const enterpriseDefaultCurrency =
                            enterpriseId != null ? getEnterpriseDefaultCurrencyCode(state, { enterpriseId }) : null;
                        const price =
                            currencyCode != null ? ratePrices[rateId]?.pricePerNight[currencyCode]?.grossValue : null;

                        const convertedPrice =
                            price != null &&
                            enterpriseDefaultCurrency != null &&
                            enterpriseId != null &&
                            currencyCode != null
                                ? convertPrice(state, {
                                      value: price,
                                      currencyCode,
                                      enterpriseId,
                                      targetCurrencyCode: enterpriseDefaultCurrency,
                                      sourceCurrencyCode: currencyCode,
                                  })
                                : null;

                        return {
                            id: rateId,
                            name: rateName,
                            price: convertedPrice,
                            currency: enterpriseDefaultCurrency,
                        };
                    }),
                    page_location: '/rates',
                    page_title: 'Rates',
                });
            },
            [SUMMARY_LOADED]: () => {
                triggerGtmEvent(AppEvent.SummaryLoaded, {
                    enterpriseName: getSelectedEnterpriseName(store.getState(), {}),
                    page_location: '/summary',
                    page_title: 'Summary',
                });
            },
            [DETAILS_LOADED]: () => {
                triggerGtmEvent(AppEvent.DetailsLoaded, {
                    enterpriseName: getSelectedEnterpriseName(store.getState(), {}),
                    page_location: '/details',
                    page_title: 'Details',
                });

                const state = store.getState();
                const reservations = getReservations(state);
                const items = pipe(
                    reservations,
                    record.collect(string.Ord)((orderId, reservation) =>
                        getGA4ItemsData(reservation, reservation.count)
                    ),
                    array.flatten
                );

                const totalValue = getReservationsTotalMultiCurrencyAmount(state);
                const merchantCurrency = getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});
                const totalValueInMerchantCurrency = totalValue?.[merchantCurrency];
                const value = totalValueInMerchantCurrency?.grossValue;
                const currency = totalValueInMerchantCurrency?.currency;

                ga4EventService.beginCheckout({ items, currency, value });
            },
            [CATEGORIES_LOADED]: ({
                payload: { categories, startDate, endDate },
            }: ActionWithPayload<string, CategoriesLoadedPayload>) => {
                const { available, unavailable } = categories;
                const state = store.getState();

                const availableItems = pipe(
                    available,
                    array.filterMap((category) =>
                        option.fromNullable(getCategoryItem({ state, category, loggerService }))
                    )
                );
                const unavailableItems = pipe(
                    unavailable,
                    array.filterMap((category) =>
                        option.fromNullable(getCategoryItem({ state, category, loggerService }))
                    )
                );

                triggerGtmEvent(AppEvent.CategoriesLoaded, {
                    startDate,
                    endDate,
                    available: availableItems,
                    unavailable: unavailableItems,
                    enterpriseName: getSelectedEnterpriseName(store.getState(), {}),
                    page_location: '/categories',
                    page_title: 'Categories',
                });

                ga4EventService.viewItemList({ items: availableItems });
            },
            [confirmationLoaded.type]: ({
                payload: {
                    reservationGroupId,
                    reservationGroupName,
                    reservationOwnerEmail,
                    orderedProducts,
                    reservations,
                    totalAmount,
                    isBookingConfirmedOrPaid,
                },
            }: ReturnType<typeof confirmationLoaded>) => {
                triggerGtmEvent(AppEvent.ConfirmationLoaded, {
                    page_location: '/confirmation',
                    page_title: 'Confirmation',
                    reservationGroupId,
                    reservationGroupName,
                    reservations: reservations.map(({ confirmationNumber, grossValue, currency }) => ({
                        confirmationNumber,
                        grossValue,
                        currency,
                    })),
                });

                const wasPurchaseEventTriggered = isPurchaseEventTriggered(reservationGroupId);

                if (isBookingConfirmedOrPaid && !wasPurchaseEventTriggered && reservations.length > 0) {
                    triggerGA4PurchaseEvent({ orderedProducts, reservations, totalAmount, reservationOwnerEmail });
                    setPurchaseEventTriggered({ contextWindow: context, reservationGroupId });
                }
            },
            [NO_AVAILABLE_OTHER_DATES_SUGGESTED]: () => {
                const state = store.getState();
                triggerLegacyGtmEvent(AppEvent.NoAvailableAlternativeDatesOffered);
                triggerGtmEvent(AppEvent.NoAvailableAlternativeDatesOffered, {
                    startDate: getStartDate(state),
                    endDate: getEndDate(state),
                });
            },
            [PRODUCT_ADDED]: (action: $TSFixMe) => productEventHandler(action, AppEvent.ProductAdded),
            [PRODUCT_REMOVED]: (action: $TSFixMe) => productEventHandler(action, AppEvent.ProductRemoved),
            [ROOM_OCCUPANCY_CHANGED]: (action: ActionWithPayload<string, CreateRoomOccupancyChangedActionOptions>) => {
                triggerLegacyGtmEvent(AppEvent.RoomOccupancyChanged, {
                    roomIndex: action.payload.roomIndex,
                    adultCount: action.payload.adultCount,
                    childCount: action.payload.childCount,
                });
            },
            [ROOM_SELECTED]: (action: ActionWithPayload<string, { roomId: string }>) => {
                const { roomId } = action.payload;
                const state = store.getState();

                const categories = getCategories(state);
                const defaultLanguageCode = getSelectedEnterpriseDefaultLanguageCode(state, {});
                const property = categories[roomId].name;
                const roomName = localizeProperty(state, { property, languageCode: defaultLanguageCode });

                triggerLegacyGtmEvent(AppEvent.RoomSelected, {
                    roomId,
                    roomName,
                    spaceType: categories[roomId].spaceType,

                    // Enhanced Ecommerce - product click
                    ecommerce: {
                        click: {
                            products: [
                                {
                                    id: roomId,
                                    name: roomName,
                                },
                            ],
                        },
                    },
                });
            },
            [CATEGORY_VIEWED]: ({ payload: { categoryId } }: { payload: { categoryId: string } }) => {
                const state = store.getState();
                const spaceCategory = getCategory(state, { categoryId });
                if (spaceCategory == null) return;

                const categoryItem = getCategoryItem({
                    state,
                    category: spaceCategory,
                    loggerService,
                });

                if (categoryItem == null) {
                    loggerService.log('ga4-category-viewed-error', 'Category not found for view_item event', {
                        spaceCategory,
                    });
                    return;
                }

                ga4EventService.viewItem({ item: categoryItem });
            },
            [RESOURCE_ORDER_ADDED]: (action: ActionWithPayload<string, { reservations: Reservations }>) => {
                const { reservations } = action.payload;

                const uniqueReservationsById = Object.values(reservations).reduce<
                    Record<string, Reservation & { count: number }>
                >((collection, reservation) => {
                    const collectedReservation = collection[reservation.orderId];

                    collection[reservation.orderId] =
                        collectedReservation != null
                            ? { ...collectedReservation, count: collectedReservation.count + 1 }
                            : { ...reservation, count: 1 };
                    return collection;
                }, {});
                const uniqueReservations = Object.values(uniqueReservationsById);

                // Enhanced Ecommerce - shopping cart add
                triggerLegacyGtmEvent(AppEvent.RoomAdded, {
                    reservations: uniqueReservations,
                    ecommerce: {
                        add: {
                            products: [
                                {
                                    id: reservations[0].roomId,
                                    quantity: reservations.length,
                                },
                            ],
                        },
                    },
                });

                triggerChangeReservationCountGA4Event(reservations[0], Object.keys(reservations).length);
            },
            [RESERVATION_COUNT_CHANGED]: (
                action: ActionWithPayload<string, { reservation: Reservation; countChange: number }>
            ) => {
                const { reservation, countChange } = action.payload;
                const { roomId, rateId, orderId } = reservation;

                // Enhanced Ecommerce - shopping cart add/remove
                triggerLegacyGtmEvent(AppEvent.RoomCountChanged, {
                    roomId,
                    rateId,
                    orderId,
                    countChange,
                    ecommerce: {
                        [countChange > 0 ? 'add' : 'remove']: {
                            products: [
                                {
                                    id: reservation.roomId,
                                    quantity: Math.abs(countChange),
                                },
                            ],
                        },
                    },
                });

                triggerChangeReservationCountGA4Event(reservation, countChange);
            },

            [ROUTE_SET]: (action: ActionWithPayload<string, { route: Route }>) => {
                const state = store.getState();

                if (isAppOpened(state)) {
                    let data = {};
                    // Enhanced Ecommerce - promotion impression
                    if (action.payload.route === Route.SpaceCategories) {
                        const voucherCode = getVoucherCode(state);

                        if (voucherCode != null && voucherCode !== '') {
                            data = {
                                ecommerce: {
                                    promoView: {
                                        id: voucherCode,
                                    },
                                },
                            };
                        }
                    }

                    const event = getEventForRoute(action.payload.route);
                    gainsightService.trackPageView({ path: `/${action.payload.route}`, title: action.payload.route });
                    updateObservabilityLabels({ route: action.payload.route });
                    if (event) {
                        triggerLegacyGtmEvent(event, data);
                    }
                }
            },
            [SPACE_TYPE_COUNT_CHANGED]: (action: ActionWithPayload<string, { count: number }>) => {
                triggerLegacyGtmEvent(AppEvent.SpaceTypeCountChanged, {
                    count: action.payload.count,
                });
            },
            [START_DATE_SET]: (action: ActionWithPayload<string, { startDate: string }>) =>
                triggerLegacyGtmEvent(AppEvent.StartDateSelected, { startDate: action.payload.startDate }),
            [VOUCHER_CODE_SET]: (action: ActionWithPayload<string, { voucherCode: string }>) =>
                triggerLegacyGtmEvent(AppEvent.PromoCodeSelected, { promoCode: action.payload.voucherCode }),
            ...createTypedEvents({
                getState: store.getState,
                triggerLegacyEvent: triggerLegacyGtmEvent,
                triggerEvent: triggerGtmEvent,
            }),

            [FLEXIBLE_RATES_FILTER_CHANGED]: (
                action: ActionWithPayload<
                    string,
                    {
                        changedByUser: boolean;
                        state: boolean;
                    }
                >
            ) => triggerGtmEvent(AppEvent.FlexibleRatesFilterChanged, action.payload),
        };

        return (next: $TSFixMe) => (action: $TSFixMe) => {
            try {
                eventMap[action.type]?.(action);
            } catch (error) {
                store.dispatch(
                    logSentryError({
                        error,
                    })
                );
            }
            return next(action);
        };

        function enqueueOrPushEvent(eventData: { [key: string]: unknown }, options?: { useNewRelic?: boolean }) {
            const event = {
                ...eventData,
                mewsSource: 'Mews Booking Engine',
                mewsAppVersion: getVersion(),
                trackingConsents: trackingConsentsService.getAllConsents(),
            };

            const logger = options?.useNewRelic === true ? loggerService : azureLoggerService;

            logger.log('event', 'gtmEvent', {
                timestamp: appClockService.getAppNowUtc().value,
                ...event,
            });

            if (gtmLoaded && trackingConsentsReady) {
                const containerId = getContainerId();
                if (containerId != null) {
                    clearDataLayer(containerId);
                }
                pushEvent(event);
            } else {
                eventQueue.push(event);
            }
        }

        function triggerEvent(prefixer: (prefix: string) => string) {
            return (event: $TSFixMe, data = {}) => {
                // bailout early if no GTM
                if (!hasTagManager) {
                    return;
                }
                const startCase = (str: string) =>
                    str
                        .replace(/\b\w/g, (match) => match.toUpperCase()) // capitalize first letter of each word
                        .replace(/([a-zA-Z])([A-Z])/g, (_match, p1, p2) => `${p1} ${p2}`); // add space before capital letter
                const eventData = {
                    ...initialEventData,
                    event: prefixer(event.name),
                    eventName: startCase(event.name),
                    ...(isHotelSelected(store.getState())
                        ? {
                              hotelName: getSelectedEnterpriseDefaultLanguageName(store.getState(), {}),
                              hotelId: getSelectedHotelId(store.getState()),
                          }
                        : {}),
                    ...data,
                };

                // push event
                enqueueOrPushEvent(eventData);
            };
        }

        function initialize() {
            const containerId = getContainerId();
            if (containerId != null) {
                hasTagManager = true;
            }
        }

        async function preserveGACookies() {
            if (isStandalone(store.getState())) {
                return preserveGA4Cookies({
                    contextWindow: context,
                    currentTimestamp: Date.now(),
                    onError: (error) =>
                        loggerService.log(
                            'google-analytics-linker-session-error',
                            'Error while parsing GA4 linker from URL',
                            {
                                error,
                                urlHref: context.location.href,
                                origin: context.origin,
                            }
                        ),
                });
            }
        }

        async function injectGtmScripts() {
            const containerId = getContainerId();
            if (containerId == null) {
                return;
            }
            try {
                await preserveGACookies();
                await injectGoogleTagManager(context, containerId);

                let time = 0;

                const interval = window.setInterval(() => {
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    gtmLoaded = context?.google_tag_manager?.dataLayer?.gtmLoad || false;

                    if (gtmLoaded) {
                        window.clearInterval(interval);
                        flushEventQueue();
                        return;
                    }

                    time += LOAD_TRY_INTERVAL;
                    if (!gtmLoaded && time >= LOAD_TIMEOUT) {
                        hasTagManager = false;
                        window.clearInterval(interval);
                        clearEventQueue();
                    }
                }, LOAD_TRY_INTERVAL);
                store.dispatch(checkGTMBlocked(containerId));
            } catch (error) {
                hasTagManager = false;
                clearEventQueue();
            }
        }

        function pushEvent(eventData: $TSFixMe) {
            const { dataLayer } = context;

            if (Array.isArray(dataLayer)) {
                // Handle safely a case when insides of push method fail, because:
                // Properties override dataLayer push with their own implementation
                // which can fail
                try {
                    dataLayer.push(eventData);
                } catch (error) {
                    store.dispatch(
                        logSentryError({
                            error,
                            extra: {
                                step: 'integrationsMiddleware pushEvent',
                                pushMethodAsString: dataLayer.push.toString(), // can handle functions
                            },
                        })
                    );
                }

                const containerId = getContainerId();
                if (containerId != null) {
                    window.setTimeout(() => clearDataLayer(containerId), 10);
                }
            }
        }

        function clearDataLayer(containerId: string) {
            try {
                context.google_tag_manager?.[containerId]?.dataLayer.reset(); // Firefox is removing google tag's container https://stackoverflow.com/questions/62478071/firefox-enhanced-privacy-protection-is-blocking-datalayer-push-to-google-tag-man
            } catch (e) {
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                if (sentryReporterService) {
                    sentryReporterService.logWarning({
                        error: new Error(`Can't reset dataLayer in integrationsMiddleware clearDataLayer()`),
                        extra: {
                            originalError: e,
                        },
                    });
                } else {
                    throw e;
                }
            }
        }

        function prefix(eventName: string) {
            return `${EVENT_PREFIX}${eventName}`;
        }

        function getEventForRoute(route: string) {
            return AppEvent.enumValues.find((e) => ('route' in e ? e.route === route : false));
        }

        function flushEventQueue() {
            if (gtmLoaded && trackingConsentsReady) {
                eventQueue.forEach(pushEvent);
                clearEventQueue();
            }
        }

        function clearEventQueue() {
            eventQueue = [];
        }

        function getContainerId() {
            return getGoogleTagManagerContainerId(store.getState());
        }
    };
}
